import { Card, CardContent, CardHeader, CardTitle } from "@shared/components/ui/card";
import { Badge } from "@shared/components/ui/badge";
import { useCitiesQuery } from "./useCities";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { MapPin, DollarSign, Clock, BarChart3 } from "lucide-react";

export function CityStatistics() {
  const { t } = useTranslation();
  const { data: citiesResponse, isLoading } = useCitiesQuery("", 1000, 1); // Get all cities for statistics

  const statistics = useMemo(() => {
    // Extract cities array from paginated response
    const cities = citiesResponse?.cities || [];

    if (!cities || cities.length === 0) {
      return {
        totalCities: 0,
        averageDeliveryFees: 0,
        averageCancellationFees: 0,
        averageRefusalFees: 0,
        fixedFeesCount: 0,
        variableFeesCount: 0,
        mostCommonDeadline: "N/A",
        highestDeliveryFees: 0,
        lowestDeliveryFees: 0,
      };
    }

    const totalCities = cities.length;
    const averageDeliveryFees =
      cities.reduce((sum, city) => sum + (city.deliveryFees || 0), 0) /
      totalCities;
    const averageCancellationFees =
      cities.reduce((sum, city) => sum + (city.cancellationFees || 0), 0) /
      totalCities;
    const averageRefusalFees =
      cities.reduce((sum, city) => sum + (city.refusalFees || 0), 0) /
      totalCities;

    const fixedFeesCount = cities.filter((city) => city.isFeesFixed).length;
    const variableFeesCount = totalCities - fixedFeesCount;

    // Find most common deadline
    const deadlineFrequency = cities.reduce((acc, city) => {
      acc[city.deadline] = (acc[city.deadline] || 0) + 1;
      return acc;
    }, {});
    const mostCommonDeadline = Object.keys(deadlineFrequency).reduce((a, b) =>
      deadlineFrequency[a] > deadlineFrequency[b] ? a : b
    );

    const deliveryFees = cities.map((city) => city.deliveryFees || 0);
    const highestDeliveryFees = Math.max(...deliveryFees);
    const lowestDeliveryFees = Math.min(...deliveryFees);

    return {
      totalCities,
      averageDeliveryFees: Number(averageDeliveryFees.toFixed(2)),
      averageCancellationFees: Number(averageCancellationFees.toFixed(2)),
      averageRefusalFees: Number(averageRefusalFees.toFixed(2)),
      fixedFeesCount,
      variableFeesCount,
      mostCommonDeadline,
      highestDeliveryFees,
      lowestDeliveryFees,
    };
  }, [citiesResponse]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="pb-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const stats = [
    {
      title: t("city.stats.totalCities"),
      value: statistics.totalCities,
      icon: MapPin,
      color: "text-blue-600",
      bgColor: "bg-blue-100 dark:bg-blue-900/20",
    },
    {
      title: t("city.stats.averageDeliveryFees"),
      value: `${statistics.averageDeliveryFees} ${t("main.currency")}`,
      icon: DollarSign,
      color: "text-green-600",
      bgColor: "bg-green-100 dark:bg-green-900/20",
    },
    {
      title: t("city.stats.mostCommonDeadline"),
      value: statistics.mostCommonDeadline,
      icon: Clock,
      color: "text-orange-600",
      bgColor: "bg-orange-100 dark:bg-orange-900/20",
    },
    {
      title: t("city.stats.feeStructure"),
      value: (
        <div className="flex gap-1">
          <Badge variant="default" className="text-xs">
            {t("city.stats.fixed")}: {statistics.fixedFeesCount}
          </Badge>
          <Badge variant="secondary" className="text-xs">
            {t("city.stats.variable")}: {statistics.variableFeesCount}
          </Badge>
        </div>
      ),
      icon: BarChart3,
      color: "text-purple-600",
      bgColor: "bg-purple-100 dark:bg-purple-900/20",
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {stats.map((stat, index) => (
        <Card key={index}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {stat.title}
            </CardTitle>
            <div className={`p-2 rounded-lg ${stat.bgColor}`}>
              <stat.icon className={`h-4 w-4 ${stat.color}`} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {typeof stat.value === "string" || typeof stat.value === "number"
                ? stat.value
                : stat.value}
            </div>
            {index === 1 && ( // Show additional info for delivery fees
              <div className="text-xs text-gray-500 mt-1">
                {t("city.stats.range")}: {statistics.lowestDeliveryFees} -{" "}
                {statistics.highestDeliveryFees} {t("main.currency")}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default CityStatistics;
